import { Card, CardContent } from "@/components/ui/card";
import Image from "next/image";
import { MenuItemCardProps } from "./types";
import EditableText from "./EditableText";
import { useState } from "react";
import { useUserStore } from "@/store/userStore";
import { CreditWarningDialog } from "./CreditWarningDialog";

export function MenuItemCard({
  item,
  language,
  isEditable = false,
  menu,
  onUpdate,
}: Omit<MenuItemCardProps, "categoryId" | "menuId" | "supportedLanguages">) {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [showCreditDialog, setShowCreditDialog] = useState(false);
  const [pendingField, setPendingField] = useState<
    "name" | "description" | null
  >(null);
  const { getRemainingCredits } = useUserStore();

  // Calculate required credits based on number of languages
  const languageCount = menu.supportedLanguages.length;
  const nameCredits = languageCount - 1; // -1 because we don't need to translate the source language
  const descriptionCredits = item.description ? languageCount - 1 : 0;

  const handleSave = async (field: "name" | "description", value: string) => {
    if (!onUpdate) return;

    const updatedItem = {
      ...item,
      [field]: {
        ...item[field],
        [language]: value,
      },
    };

    await onUpdate(updatedItem);
    setEditingField(null);
  };

  const handleEdit = (field: "name" | "description") => {
    const remainingCredits = getRemainingCredits();
    const requiredCredits = field === "name" ? nameCredits : descriptionCredits;

    if (requiredCredits > 0 && remainingCredits < requiredCredits) {
      setPendingField(field);
      setShowCreditDialog(true);
      return;
    }

    setEditingField(field);
  };

  return (
    <>
      <Card className="hover:shadow-md transition-shadow h-full">
        <CardContent className="flex gap-4 p-4 h-full">
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="flex-1">
              <div>
                <EditableText
                  value={item.name[language] || ""}
                  onSave={(value) => handleSave("name", value)}
                  isEditing={editingField === "name"}
                  onEdit={() => handleEdit("name")}
                  onCancel={() => setEditingField(null)}
                  className="font-medium text-base"
                  isEditable={isEditable}
                  requiredCredits={nameCredits}
                  showCreditWarning={false} // We handle credit warning at this level now
                  supportedLanguages={menu.supportedLanguages}
                  currentLanguage={language}
                />
                {item.description && (
                  <EditableText
                    value={item.description?.[language] || ""}
                    onSave={(value) => handleSave("description", value)}
                    isEditing={editingField === "description"}
                    onEdit={() => handleEdit("description")}
                    onCancel={() => setEditingField(null)}
                    className="text-sm text-muted-foreground mt-1 line-clamp-2"
                    textComponent="textarea"
                    isEditable={isEditable}
                    requiredCredits={descriptionCredits}
                    showCreditWarning={false} // We handle credit warning at this level now
                    supportedLanguages={menu.supportedLanguages}
                    currentLanguage={language}
                  />
                )}
              </div>
            </div>
            <div className="mt-auto pt-4">
              <EditableText
                value={
                  item.price.decimalPlaces !== undefined &&
                  item.price.decimalPlaces > 0
                    ? item.price.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : item.price.amount.toLocaleString()
                }
                onSave={() => Promise.resolve()}
                isEditing={false}
                onEdit={() => {}}
                onCancel={() => {}}
                className="font-medium"
                isEditable={isEditable}
                showCreditWarning={false} // Price doesn't need translation
                prefix={menu.currency || "¥"}
              />
              {item.price.taxIncluded && (
                <p className="text-xs text-muted-foreground">
                  (税込 {menu.currency || "¥"}
                  {item.price.decimalPlaces !== undefined &&
                  item.price.decimalPlaces > 0
                    ? item.price.taxIncluded.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : item.price.taxIncluded.toLocaleString()}
                  )
                </p>
              )}
            </div>
          </div>
          {item.imageUrl && (
            <div className="relative w-24 h-24 flex-shrink-0">
              <Image
                src={item.imageUrl}
                alt={`${
                  item.name[language] || item.name.japanese || "Menu item"
                } - food image`}
                fill
                className="object-cover rounded-md"
                sizes="96px"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <CreditWarningDialog
        open={showCreditDialog}
        onOpenChange={setShowCreditDialog}
        requiredCredits={
          pendingField === "name" ? nameCredits : descriptionCredits
        }
        supportedLanguages={menu.supportedLanguages}
        currentLanguage={language}
        onProceed={(selectedLanguages) => {
          setShowCreditDialog(false);
          if (selectedLanguages.length > 0 && pendingField) {
            setEditingField(pendingField);
          }
          setPendingField(null);
        }}
      />
    </>
  );
}

export default MenuItemCard;
